import { useEffect, useMemo, useState } from "react";
import ListTotalsFooter from "../../components/ListTotalsFooter";
import { Grid, Typography, Box, TextField, Chip, Button, Tooltip } from "@mui/material";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ClearIcon from "@mui/icons-material/Clear";
import { FileDownload } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { MaterialReactTable } from "material-react-table";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { fitColumns } from "../../utils/excelFit";
import Layout from "../../layout/Layout";
import { apiData } from "../../services/api_service";
import { damagedReportAPI } from "../../config";
import { formatNumber, formatAED } from "../../utils/format";
import ListFilterBar, { emptyListFilters } from "../../components/ListFilterBar";
import { writeXlsxFrozen } from "../../utils/xlsxFreeze";

const TODAY_STR = dayjs().format("YYYY-MM-DD");
const MONTH_START_STR = dayjs().startOf("month").format("YYYY-MM-DD");

// Modern Shiny Shimmer Skeleton Component
const ShinySkeleton = ({ width, height, borderRadius = 6, style = {} }) => (
  <Box
    sx={{
      width: width || "100%",
      height: height || 20,
      borderRadius: typeof borderRadius === "number" ? `${borderRadius}px` : borderRadius,
      background: "linear-gradient(90deg, #e2e8f0 0%, #f8fafc 35%, #ffffff 50%, #f8fafc 65%, #e2e8f0 100%)",
      backgroundSize: "400% 100%",
      animation: "shinyShimmer 1.4s cubic-bezier(0.4, 0, 0.2, 1) infinite",
      boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
      "@keyframes shinyShimmer": {
        "0%": { backgroundPosition: "100% 0%" },
        "100%": { backgroundPosition: "0% 0%" },
      },
      ...style,
    }}
  />
);

export default function DamagedProductsReport() {
  const navigate = useNavigate();
  const location = useLocation();

  // Get filters from navigation state
  const storeId = location.state?.store_id || location.state?.storeId;
  const productId = location.state?.product_id || location.state?.productId;
  const productName = location.state?.product_name || location.state?.productName;
  const storeName = location.state?.store_name || location.state?.storeName;
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totals, setTotals] = useState({ qty: 0, value: 0 });
  const [loading, setLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [globalFilter, setGlobalFilter] = useState(() => location.state?.search || location.state?.globalSearch || "");
  const [columnFilters, setColumnFilters] = useState([]);
  // Store / performed-by filters (store may arrive pre-set from a drill-down).
  const [bar, setBar] = useState({ ...emptyListFilters, storeId: storeId || 0 });
  
  const [dateFrom, setDateFrom] = useState(() => {
    if (location.state?.fromDate !== undefined) {
      return location.state.fromDate;
    }
    if (storeId && productId) {
      return "";
    }
    return MONTH_START_STR;
  });

  const [dateTo, setDateTo] = useState(() => {
    if (location.state?.toDate !== undefined) {
      return location.state.toDate;
    }
    if (storeId && productId) {
      return "";
    }
    return TODAY_STR;
  });

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await apiData("get", damagedReportAPI, {
        pageIndex,
        pageSize,
        globalSearch: globalFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        store_id: Number(bar.storeId) > 0 ? bar.storeId : undefined,
        sales_person_id: Number(bar.salesPersonId) > 0 ? bar.salesPersonId : undefined,
        product_id: productId || undefined,
        columnFilters: columnFilters.length > 0 ? JSON.stringify(columnFilters) : undefined,
      });
      setRows(res.data?.result || []);
      setTotalCount(res.data?.totalCount || 0);
      setTotals({
        qty: res.data?.totalDamagedQty || 0,
        value: res.data?.totalDamagedValue || 0,
      });
    } catch (e) {
      console.error("Failed to fetch damaged report:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize, globalFilter, columnFilters, dateFrom, dateTo, bar, productId]);

  // Keep the range valid as the user edits it: neither end can go past
  // today, and From can never land after To (or vice versa) — nudge the
  // other field along instead of allowing an inverted/impossible range.
  const handleDateFromChange = (value) => {
    setPageIndex(0);
    setDateFrom(value);
    if (value && dateTo && value > dateTo) {
      setDateTo(value);
    }
  };

  const handleDateToChange = (value) => {
    setPageIndex(0);
    setDateTo(value);
    if (value && dateFrom && value < dateFrom) {
      setDateFrom(value);
    }
  };

  const handleClearDates = () => {
    setPageIndex(0);
    setDateFrom("");
    setDateTo("");
  };

  // Mirrors fetchReport's payload exactly (same date range, store/product
  // scope, column filters, global search) but with an effectively unlimited
  // page size, so the export always matches what's currently filtered on
  // screen rather than just the current page.
  const handleExportData = async () => {
    setLoading(true);
    try {
      const res = await apiData("get", damagedReportAPI, {
        pageIndex: 0,
        pageSize: 1000000,
        globalSearch: globalFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        store_id: Number(bar.storeId) > 0 ? bar.storeId : undefined,
        sales_person_id: Number(bar.salesPersonId) > 0 ? bar.salesPersonId : undefined,
        product_id: productId || undefined,
        columnFilters: columnFilters.length > 0 ? JSON.stringify(columnFilters) : undefined,
      });
      const result = res.data?.result || [];
      if (!result.length) {
        alert("No data to export!");
        return;
      }

      const exportData = result.map((item, index) => ({
        "#": index + 1,
        Date: item.event_date ? dayjs(item.event_date).format("DD-MM-YYYY") : "",
        Reference: item.reference_number || "",
        "Reference Type": item.reference_type === "grn" ? "GRN" : "Credit Note",
        "Invoice / CDN": item.order_number || "",
        Product: item.product_name || "",
        SKU: item.sku || "",
        Store: item.store_name || "",
        Customer: item.customer_name || "",
        "GRN Created By": item.created_by_name || "",
        "GRN Created At": item.reference_created_at
          ? dayjs(item.reference_created_at).format("DD-MM-YYYY")
          : "",
        "Damaged Qty": Number(item.quantity) || 0,
        Value: Number(item.line_total) || 0,
        "Damage Note": item.damaged_reason || "",
      }));

      // Total footer — use the SAME backend aggregates the on-screen footer
      // shows (totals over ALL filtered rows, never the current page), so the
      // Excel footer matches the list footer.
      exportData.push({
        Product: "Total",
        "Damaged Qty": Number(res.data?.totalDamagedQty) || 0,
        Value: Number(res.data?.totalDamagedValue) || 0,
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      {
        const c = Object.keys(exportData[0] || {}).indexOf("Value");
        for (let r = 1; c >= 0 && r <= exportData.length; r += 1) {
          const cell = ws[XLSX.utils.encode_cell({ r, c })];
          if (cell && cell.t === "n") cell.z = '"AED" #,##0.00';
        }
      }
      fitColumns(ws);
      XLSX.utils.book_append_sheet(wb, ws, "Damaged_Products_Report");
      writeXlsxFrozen(XLSX, 
        wb,
        `Damaged_Products_Report_${dayjs().format("DD_MM_YYYY")}.xlsx`
      );
    } catch (e) {
      console.error("Export failed:", e);
      alert("Failed to export data.");
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "event_date",
        header: "Date",
        enableColumnFilter: true,
        Cell: ({ cell }) =>
          cell.getValue()
            ? dayjs(cell.getValue()).format("DD-MM-YYYY")
            : "-",
      },
      {
        accessorKey: "reference_number",
        header: "Reference",
        enableColumnFilter: true,
        // Damaged stock comes from two flows: a Tax Credit Note against a
        // real sale, or a GRN raised against a CDN that was never invoiced.
        // Show whichever produced this row, tagged so it's unambiguous.
        // GRNs have a real receipt (reference_pdf_url) once generated, so
        // the number links straight to it, same as the CDN list.
        Cell: ({ row }) => {
          const isGrn = row.original.reference_type === "grn";
          const pdfUrl = row.original.reference_pdf_url;
          const hasPdf = pdfUrl && pdfUrl !== "NA" && String(pdfUrl).trim() !== "";
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              {hasPdf ? (
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#1976d2", fontWeight: 600, textDecoration: "none" }}
                >
                  {row.original.reference_number || "-"}
                </a>
              ) : (
                <span>{row.original.reference_number || "-"}</span>
              )}
              <Chip
                size="small"
                label={isGrn ? "GRN" : "Credit Note"}
                color={isGrn ? "info" : "default"}
                variant="outlined"
                sx={{ height: 18, fontSize: 10 }}
              />
            </Box>
          );
        },
      },
      {
        accessorKey: "order_number",
        header: "Invoice / CDN",
        enableColumnFilter: true,
        // The invoice (credit-note flow) or CDN (GRN flow) this damaged
        // line came from — links to that document's own PDF, not the GRN/
        // credit-note PDF in the Reference column.
        Cell: ({ row }) => {
          const num = row.original.order_number;
          const url = row.original.order_pdf_url;
          if (!num) return "-";
          return url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2", fontWeight: 600, textDecoration: "none" }}
            >
              {num}
            </a>
          ) : (
            num
          );
        },
      },
      {
        accessorKey: "product_name",
        header: "Product",
        enableColumnFilter: true,
      },
      {
        accessorKey: "sku",
        header: "SKU",
        enableColumnFilter: true,
      },
      {
        accessorKey: "store_name",
        header: "Store",
        enableColumnFilter: true,
      },
      {
        accessorKey: "customer_name",
        header: "Customer",
        enableColumnFilter: true,
      },
      {
        // Who/when raised the GRN (or Credit Note, for that flow) this
        // damaged line came from — not the return/GRN date shown in "Date".
        accessorKey: "created_by_name",
        header: "GRN Created By",
        enableColumnFilter: true,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "reference_created_at",
        header: "GRN Created At",
        enableColumnFilter: true,
        Cell: ({ cell }) =>
          cell.getValue() ? dayjs(cell.getValue()).format("DD-MM-YYYY") : "-",
      },
      {
        accessorKey: "quantity",
        header: "Damaged Qty",
        enableColumnFilter: true,
        Cell: ({ row }) => formatNumber(row.original.quantity),
      },
      {
        accessorKey: "line_total",
        header: "Value",
        enableColumnFilter: true,
        Cell: ({ cell }) => formatAED(cell.getValue()),
      },
      {
        accessorKey: "damaged_reason",
        header: "Damage Note",
        enableColumnFilter: true,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
    ],
    [],
  );

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6} md={6} className="form-lable">
            <Typography className="page_title" textAlign="left">
              Damaged Products Report
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              {storeName && (
                <Link underline="hover" color="inherit" href="/reports/stock-report">
                  Stock Inventory Report
                </Link>
              )}
              {storeName && (
                <Typography color="text.primary">
                  {storeName} {productName ? `- ${productName}` : ""}
                </Typography>
              )}
              {!storeName && (
                <Typography color="text.primary">Damaged Products</Typography>
              )}
            </Breadcrumbs>
          </Grid>
          <Grid item xs={12} lg={6} md={6} textAlign="right" className="form-lable">
            <LoadingButton
              size="medium"
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </LoadingButton>
          </Grid>
        </Grid>

        <ListFilterBar
          value={bar}
          onChange={(v) => {
            setPageIndex(0);
            setBar(v);
          }}
          onClear={() => setBar({ ...emptyListFilters })}
          showCustomer={false}
          showDates={false}
          showSearch={false}
        />

        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
            my: 2,
          }}
        >
          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={dateFrom}
            onChange={(e) => handleDateFromChange(e.target.value)}
            inputProps={{ max: dateTo || TODAY_STR }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={dateTo}
            onChange={(e) => handleDateToChange(e.target.value)}
            inputProps={{ min: dateFrom || undefined, max: TODAY_STR }}
          />
          {(dateFrom || dateTo) && (
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearDates}
              sx={{ textTransform: "none" }}
            >
              Clear
            </Button>
          )}
        </Box>

        <Grid className="form_design">
          <MaterialReactTable
            columns={columns}
            data={rows}
            enableColumnFilters={true}
            enableFullScreenToggle={false}
            enableStickyHeader
            manualPagination
            rowCount={totalCount}
            state={{
              isLoading: loading && rows.length === 0,
              showProgressBars: loading,
              pagination: { pageIndex, pageSize },
              globalFilter,
              columnFilters,
            }}
            renderBottomToolbarCustomActions={() => (
              <ListTotalsFooter
                items={[
                  { label: `Total (${totalCount} ${totalCount === 1 ? "record" : "records"}) - Damaged Qty`, value: formatNumber(totals.qty) },
                  { label: "Damaged Value", value: `${formatAED(totals.value)}`, tone: "red" },
                ]}
              />
            )}
            renderTopToolbarCustomActions={() => (
              <Tooltip title="Export data to excel sheet" arrow>
                <Button
                  onClick={handleExportData}
                  startIcon={<FileDownload />}
                  sx={{ background: "#1a73e829", textTransform: "none" }}
                >
                  Export To Excel
                </Button>
              </Tooltip>
            )}
            onGlobalFilterChange={setGlobalFilter}
            onColumnFiltersChange={setColumnFilters}
            onPaginationChange={(updater) => {
              const s = updater({ pageIndex, pageSize });
              setPageIndex(s.pageIndex);
              setPageSize(s.pageSize);
            }}
            muiTablePaginationProps={{ rowsPerPageOptions: [100] }}
            muiTableContainerProps={{
              sx: { maxHeight: "calc(100vh - 300px)", minHeight: 450 },
            }}
          />
        </Grid>
      </Grid>
    </Layout>
  );
}
